import React, { useState, useEffect } from 'react';
import { Book, ChevronDown, ChevronRight, FileText, Users, FileSpreadsheet, Phone, BarChart, Settings, HelpCircle, FileDown, ArrowUp, Menu, X } from 'lucide-react';

const Documentation = () => {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    userManagement: true,
    campaignManagement: true,
    callManagement: true,
    reports: true,
    systemSettings: true
  });
  
  // Nuovo stato per gestire la visibilità della sidebar su mobile
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  
  // Nuovo stato per gestire la visibilità del pulsante "scroll to top"
  const [showScrollTop, setShowScrollTop] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  
  // Funzione per scorrere verso l'alto
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Effetto per gestire la visibilità del pulsante "scroll to top"
  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {/* Stili per la stampa */}
      <style>
        {`
          @media print {
            body {
              font-size: 12pt;
              color: #000;
              background-color: #fff;
            }
            
            .bg-gray-50, .bg-white {
              background-color: #fff !important;
              box-shadow: none !important;
            }
            
            .md\\:col-span-1 {
              display: none !important;
            }
            
            .md\\:col-span-3 {
              width: 100% !important;
              max-width: 100% !important;
              flex: 0 0 100% !important;
            }
            
            button, a[href]:not([href^="#"]) {
              display: none !important;
            }
            
            a[href^="#"] {
              color: #000 !important;
              text-decoration: none !important;
            }
            
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
              page-break-inside: avoid;
            }
            
            p, li {
              page-break-inside: avoid;
            }
            
            section {
              page-break-inside: avoid;
              margin-bottom: 1cm;
            }
            
            .border-t {
              border-top: 1px solid #000 !important;
            }
            
            .text-indigo-500, .text-indigo-600, .text-indigo-700 {
              color: #000 !important;
            }
            
            .text-gray-500, .text-gray-600, .text-gray-700, .text-gray-800, .text-gray-900 {
              color: #000 !important;
            }
            
            .bg-blue-50, .bg-yellow-50 {
              background-color: #fff !important;
              border: 1px solid #000 !important;
              border-radius: 0 !important;
            }
            
            .text-blue-700, .text-yellow-700 {
              color: #000 !important;
            }
            
            /* Mostra l'indice solo nella stampa */
            .print-only {
              display: block !important;
              page-break-after: avoid;
            }
            
            /* Stile per il salto pagina */
            .page-break {
              page-break-after: always;
              height: 0;
              display: block;
            }
            
            /* Margini di pagina semplici */
            @page {
              margin: 2cm;
            }
            
            /* Nascondi il footer nella versione stampata */
            .border-t.border-gray-200.pt-6.mt-10 {
              display: none !important;
            }
            
            /* Assicurati che gli stili inline vengano preservati nella stampa */
            [style] {
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
            }
          }
        `}
      </style>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Copertina per la stampa - visibile solo nella versione PDF */}
        <div className="print-only hidden">
          <div className="flex flex-col items-center justify-center min-h-screen">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-8">
                <div style={{
                  background: 'linear-gradient(to right, #4f46e5, #3730a3)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  padding: '1rem',
                  borderRadius: '0.5rem'
                }}>
                  <img src="/bonobo.png" alt="Bonobo CRM Logo" className="w-40 h-40 object-contain" />
                </div>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Bonobo CRM</h1>
              <h2 className="text-2xl font-semibold text-gray-700 mb-8">Systemdokumentation</h2>
              <p className="text-md text-gray-500">
                Letzte Aktualisierung: {new Date().toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>
        
        <header className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
              <Book className="mr-2 sm:mr-3 h-6 w-6 sm:h-8 sm:w-8 text-indigo-500" />
              Systemdokumentation
            </h1>
            <div className="flex space-x-3">
              <button 
                onClick={() => window.print()}
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm sm:text-base"
              >
                <FileDown className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                PDF herunterladen
              </button>
              <a 
                href="/" 
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm sm:text-base"
              >
                Zurück zum CRM
              </a>
            </div>
          </div>
          <p className="mt-2 text-base sm:text-lg text-gray-600">
            Umfassende Dokumentation für das Bonobo CRM-System
          </p>
        </header>

        {/* Indice per la stampa - visibile solo nella versione PDF */}
        <div className="print-only hidden mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">Inhaltsverzeichnis</h2>
          <div className="border-t border-b border-gray-300 py-6">
            <ul className="space-y-3">
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">1.</span>
                  <a href="#system-overview" className="text-gray-800 hover:text-indigo-600">Systemübersicht</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">3</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">2.</span>
                  <a href="#getting-started" className="text-gray-800 hover:text-indigo-600">Erste Schritte</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">4</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">3.</span>
                  <a href="#user-roles" className="text-gray-800 hover:text-indigo-600">Benutzerrollen</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">5</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">4.</span>
                  <a href="#user-creation" className="text-gray-800 hover:text-indigo-600">Benutzer erstellen</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">6</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">5.</span>
                  <a href="#3cx-integration" className="text-gray-800 hover:text-indigo-600">3CX-Integration</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">7</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">6.</span>
                  <a href="#campaign-creation" className="text-gray-800 hover:text-indigo-600">Kampagne erstellen</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">8</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">7.</span>
                  <a href="#customer-import" className="text-gray-800 hover:text-indigo-600">Kundenimport</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">9</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">8.</span>
                  <a href="#customer-export" className="text-gray-800 hover:text-indigo-600">Kundenexport</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">10</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">9.</span>
                  <a href="#call-protocol" className="text-gray-800 hover:text-indigo-600">Anrufprotokoll</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">11</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">10.</span>
                  <a href="#call-recordings" className="text-gray-800 hover:text-indigo-600">Anrufaufzeichnungen</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">12</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">11.</span>
                  <a href="#reports-statistics" className="text-gray-800 hover:text-indigo-600">Berichte und Statistiken</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">13</span>
              </li>
              <li className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="mr-3 font-medium">12.</span>
                  <a href="#password-change" className="text-gray-800 hover:text-indigo-600">Passwort ändern</a>
                </div>
                <div className="border-b border-dotted border-gray-400 flex-grow mx-4"></div>
                <span className="text-gray-600">14</span>
              </li>
            </ul>
          </div>
          <div className="page-break"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
          {/* Pulsante per mostrare/nascondere la sidebar su mobile */}
          <div className="md:hidden mb-4">
            <button 
              onClick={() => setShowMobileSidebar(!showMobileSidebar)}
              className="flex items-center justify-center w-full bg-white shadow-md rounded-lg p-3 text-indigo-600 hover:bg-indigo-50 transition-colors"
            >
              {showMobileSidebar ? (
                <>
                  <X className="h-5 w-5 mr-2" />
                  Inhaltsverzeichnis schließen
                </>
              ) : (
                <>
                  <Menu className="h-5 w-5 mr-2" />
                  Inhaltsverzeichnis anzeigen
                </>
              )}
            </button>
          </div>
          
          {/* Sidebar - Indice */}
          <div className={`${showMobileSidebar ? 'block' : 'hidden'} md:block md:col-span-1`}>
            <div className="bg-white shadow-md rounded-lg p-4 md:sticky md:top-4">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Inhaltsverzeichnis</h2>
              
              <nav className="space-y-1">
                {/* Sezione Panoramica */}
                <div>
                  <button 
                    onClick={() => toggleSection('overview')}
                    className="flex items-center w-full text-left px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    {expandedSections.overview ? 
                      <ChevronDown className="h-4 w-4 mr-2 text-gray-500" /> : 
                      <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                    }
                    <FileText className="h-4 w-4 mr-2 text-indigo-500" />
                    Übersicht
                  </button>
                  
                  {expandedSections.overview && (
                    <div className="pl-8 mt-1 space-y-1">
                      <a href="#system-overview" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Systemübersicht
                      </a>
                      <a href="#getting-started" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Erste Schritte
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Sezione Utenti */}
                <div>
                  <button 
                    onClick={() => toggleSection('userManagement')}
                    className="flex items-center w-full text-left px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    {expandedSections.userManagement ? 
                      <ChevronDown className="h-4 w-4 mr-2 text-gray-500" /> : 
                      <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                    }
                    <Users className="h-4 w-4 mr-2 text-indigo-500" />
                    Benutzerverwaltung
                  </button>
                  
                  {expandedSections.userManagement && (
                    <div className="pl-8 mt-1 space-y-1">
                      <a href="#user-roles" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Benutzerrollen
                      </a>
                      <a href="#user-creation" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Benutzer erstellen
                      </a>
                      <a href="#3cx-integration" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        3CX-Integration
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Sezione Campagne */}
                <div>
                  <button 
                    onClick={() => toggleSection('campaignManagement')}
                    className="flex items-center w-full text-left px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    {expandedSections.campaignManagement ? 
                      <ChevronDown className="h-4 w-4 mr-2 text-gray-500" /> : 
                      <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                    }
                    <FileSpreadsheet className="h-4 w-4 mr-2 text-indigo-500" />
                    Kampagnenverwaltung
                  </button>
                  
                  {expandedSections.campaignManagement && (
                    <div className="pl-8 mt-1 space-y-1">
                      <a href="#campaign-creation" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Kampagne erstellen
                      </a>
                      <a href="#customer-import" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Kundenimport
                      </a>
                      <a href="#customer-export" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Kundenexport
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Sezione Chiamate */}
                <div>
                  <button 
                    onClick={() => toggleSection('callManagement')}
                    className="flex items-center w-full text-left px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    {expandedSections.callManagement ? 
                      <ChevronDown className="h-4 w-4 mr-2 text-gray-500" /> : 
                      <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                    }
                    <Phone className="h-4 w-4 mr-2 text-indigo-500" />
                    Anrufverwaltung
                  </button>
                  
                  {expandedSections.callManagement && (
                    <div className="pl-8 mt-1 space-y-1">
                      <a href="#call-protocol" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Anrufprotokoll
                      </a>
                      <a href="#call-recordings" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Anrufaufzeichnungen
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Sezione Report */}
                <div className="mb-2">
                  <button
                    onClick={() => toggleSection('reports')}
                    className="flex items-center w-full text-left px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    {expandedSections.reports ? 
                      <ChevronDown className="h-4 w-4 mr-2 text-gray-500" /> : 
                      <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                    }
                    <BarChart className="h-4 w-4 mr-2 text-indigo-500" />
                    Berichte und Statistiken
                  </button>
                  {expandedSections.reports && (
                    <div className="pl-8 mt-1 space-y-1">
                      <a href="#reports-statistics" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Verfügbare Statistiken
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Sezione Impostazioni */}
                <div className="mb-2">
                  <button
                    onClick={() => toggleSection('systemSettings')}
                    className="flex items-center w-full text-left px-2 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    {expandedSections.systemSettings ? 
                      <ChevronDown className="h-4 w-4 mr-2 text-gray-500" /> : 
                      <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                    }
                    <Settings className="h-4 w-4 mr-2 text-indigo-500" />
                    Passwort ändern
                  </button>
                  {expandedSections.systemSettings && (
                    <div className="pl-8 mt-1 space-y-1">
                      <a href="#password-change" className="block px-2 py-1 text-sm text-gray-600 hover:text-indigo-600">
                        Passwort ändern
                      </a>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          </div>
          
          {/* Contenuto principale */}
          <div className="md:col-span-3">
            <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
              <section id="system-overview" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-indigo-500" />
                  Systemübersicht
                </h2>
                <div className="prose max-w-none">
                  <p className="text-gray-700">
                    Willkommen zur Dokumentation des CRM-Systems. Diese Dokumentation bietet einen umfassenden Überblick über alle Funktionen und Module des Systems.
                  </p>
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <HelpCircle className="h-5 w-5 text-blue-500" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Diese Dokumentation wird kontinuierlich erweitert und aktualisiert.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="getting-started" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-indigo-500" />
                  Erste Schritte
                </h2>
                <p className="text-gray-700">
                  Das CRM-System ermöglicht die effiziente Verwaltung von Kundenkontakten und Kampagnen.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Grundlegende Funktionen</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <span className="font-medium">Kampagnen</span>: Organisieren Sie Ihre Kundenkontakte in Kampagnen.
                    </li>
                    <li>
                      <span className="font-medium">Kundenverwaltung</span>: Verwalten Sie Kundendaten und Anrufhistorie.
                    </li>
                    <li>
                      <span className="font-medium">Anrufe</span>: Tätigen Sie Anrufe direkt aus dem CRM und erfassen Sie Feedback.
                    </li>
                    <li>
                      <span className="font-medium">Statistiken</span>: Analysieren Sie die Leistung von Kampagnen und Agenten.
                    </li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>Tipp:</strong> Beginnen Sie mit der Auswahl einer Kampagne im Hauptmenü, um die zugehörigen Kunden anzuzeigen.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="user-roles" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="mr-2 h-5 w-5 text-indigo-500" />
                  Benutzerrollen
                </h2>
                <p className="text-gray-700">
                  Das System unterstützt verschiedene Benutzerrollen mit unterschiedlichen Berechtigungen.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Verfügbare Rollen</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <span className="font-medium">Administrator</span>: Hat vollen Zugriff auf alle Funktionen des Systems, einschließlich Benutzerverwaltung und Kampagnenverwaltung.
                    </li>
                    <li>
                      <span className="font-medium">Agent</span>: Kann Kundendaten einsehen, Anrufe tätigen und Feedback erfassen.
                    </li>
                  </ul>
                </div>
              </section>
              
              <section id="user-creation" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="mr-2 h-5 w-5 text-indigo-500" />
                  Benutzer erstellen
                </h2>
                <p className="text-gray-700">
                  Administratoren können neue Benutzer im System anlegen und deren Rollen festlegen.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Schritte zur Benutzererstellung</h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Navigieren Sie zur Benutzerverwaltung über das Hauptmenü.</li>
                    <li>Klicken Sie auf die Schaltfläche "Benutzer hinzufügen".</li>
                    <li>Füllen Sie das Formular mit den erforderlichen Informationen aus:
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Benutzername</li>
                        <li>E-Mail-Adresse</li>
                        <li>Passwort</li>
                        <li>Rolle (Administrator oder Agent)</li>
                        <li>Status (Aktiv oder Inaktiv)</li>
                      </ul>
                    </li>
                    <li>Optional: Fügen Sie 3CX-Operatorinformationen hinzu, wenn der Benutzer mit dem Telefonsystem verbunden werden soll.</li>
                    <li>Klicken Sie auf "Benutzer erstellen", um den Vorgang abzuschließen.</li>
                  </ol>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Hinweis:</strong> Passwörter sollten mindestens 8 Zeichen lang sein und Buchstaben, Zahlen und Sonderzeichen enthalten.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="3cx-integration" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="mr-2 h-5 w-5 text-indigo-500" />
                  3CX-Integration
                </h2>
                <p className="text-gray-700">
                  Das CRM-System kann mit dem 3CX-Telefonsystem integriert werden, um Anrufaufzeichnungen und Anrufinformationen zu synchronisieren.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Benutzer mit 3CX-Operatoren verknüpfen</h3>
                  <p className="text-gray-700 mb-3">
                    Um Anrufaufzeichnungen korrekt zuzuordnen, müssen CRM-Benutzer mit 3CX-Operatoren verknüpft werden:
                  </p>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Bearbeiten Sie einen Benutzer in der Benutzerverwaltung.</li>
                    <li>Geben Sie die 3CX-Operator-ID im entsprechenden Feld ein (z.B. "063").</li>
                    <li>Optional: Fügen Sie den Namen des 3CX-Operators hinzu.</li>
                    <li>Speichern Sie die Änderungen.</li>
                  </ol>
                </div>
              </section>
              
              <section id="campaign-creation" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileSpreadsheet className="mr-2 h-5 w-5 text-indigo-500" />
                  Kampagne erstellen
                </h2>
                <p className="text-gray-700">
                  Kampagnen sind die Grundlage für die Organisation von Kundendaten und Anrufaktivitäten im CRM-System.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Schritte zur Kampagnenerstellung</h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Navigieren Sie zur Kampagnenverwaltung über das Hauptmenü.</li>
                    <li>Klicken Sie auf die Schaltfläche "Neue Kampagne".</li>
                    <li>Geben Sie einen Namen für die Kampagne ein.</li>
                    <li>Fügen Sie optional eine Beschreibung hinzu.</li>
                    <li>Legen Sie den Status der Kampagne fest (aktiv oder inaktiv).</li>
                    <li>Klicken Sie auf "Kampagne erstellen", um den Vorgang abzuschließen.</li>
                  </ol>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Kampagnen verwalten</h3>
                  <p className="text-gray-700 mb-3">
                    In der Kampagnenverwaltung können Sie:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Bestehende Kampagnen bearbeiten</li>
                    <li>Kampagnendetails anzeigen</li>
                    <li>Kunden zu einer Kampagne importieren</li>
                    <li>Kampagnen aktivieren oder deaktivieren</li>
                  </ul>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Hinweis:</strong> Die Anzeige inaktiver Kampagnen kann sowohl in der Kampagnenverwaltung als auch im CRM-Hauptmenü über eine Umschaltfläche aktiviert oder deaktiviert werden.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="customer-import" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileSpreadsheet className="mr-2 h-5 w-5 text-indigo-500" />
                  Kundenimport
                </h2>
                <p className="text-gray-700">
                  Kundendaten können in Kampagnen importiert werden, um Anrufaktivitäten zu organisieren.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Datenimport-Prozess</h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Öffnen Sie die Kampagnenverwaltung über das Hauptmenü.</li>
                    <li>Wählen Sie eine Kampagne aus und klicken Sie auf das Import-Symbol.</li>
                    <li>Klicken Sie auf "Datei auswählen" und wählen Sie eine Excel-Datei (XLSX) mit Kundendaten aus.</li>
                    <li>Das System zeigt eine Vorschau der zu importierenden Daten an.</li>
                    <li>Überprüfen Sie die Daten und klicken Sie auf "Import starten", um den Vorgang zu beginnen.</li>
                    <li>Warten Sie, bis der Import abgeschlossen ist. Das System zeigt den Fortschritt an.</li>
                  </ol>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>Format:</strong> Die Excel-Datei sollte folgende Spalten enthalten:
                      </p>
                      <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full border border-blue-200 rounded">
                          <thead>
                            <tr className="bg-blue-100">
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Name</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Phone</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Vertragsdatum</th>
                              <th className="px-4 py-2 border-b border-blue-200 text-left text-xs font-medium text-blue-800">Interne Nr.</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="px-4 py-2 border-b border-r border-blue-200 text-xs text-blue-700">Max Mustermann</td>
                              <td className="px-4 py-2 border-b border-r border-blue-200 text-xs text-blue-700">+49 123 456789</td>
                              <td className="px-4 py-2 border-b border-r border-blue-200 text-xs text-blue-700">01/01/2023</td>
                              <td className="px-4 py-2 border-b border-blue-200 text-xs text-blue-700">12345</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="mt-2 text-xs text-blue-700">
                        <strong>Wichtig:</strong> Die Spaltennamen müssen exakt wie oben angegeben sein.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="customer-export" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <FileSpreadsheet className="mr-2 h-5 w-5 text-indigo-500" />
                  Kundenexport
                </h2>
                <p className="text-gray-700">
                  Kundendaten können aus Kampagnen exportiert werden, um sie in externen Anwendungen zu verwenden oder zu archivieren.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Datenexport-Prozess</h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Öffnen Sie die Kampagnenverwaltung über das Hauptmenü.</li>
                    <li>Wählen Sie eine Kampagne aus und klicken Sie auf das Export-Symbol (Download-Icon).</li>
                    <li>Im Export-Dialog können Sie optional Filter anwenden:
                      <ul className="list-disc pl-5 mt-2 space-y-1">
                        <li>Nach Status filtern (Offen, Verkauft, Geschlossen, Nicht erreicht)</li>
                        <li>Nach Feedback filtern (Positiv, Neutral, Negativ)</li>
                      </ul>
                    </li>
                    <li>Klicken Sie auf "Als XLSX exportieren", um den Export zu starten.</li>
                    <li>Die Datei wird automatisch heruntergeladen und enthält alle Kundendaten der Kampagne, die den ausgewählten Filterkriterien entsprechen.</li>
                  </ol>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>Format:</strong> Die exportierte Excel-Datei enthält folgende Spalten:
                      </p>
                      <div className="mt-2 overflow-x-auto">
                        <table className="min-w-full border border-blue-200 rounded">
                          <thead>
                            <tr className="bg-blue-100">
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">ID</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Name</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Telefon</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Interne Nummer</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Status</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Feedback</th>
                              <th className="px-4 py-2 border-b border-r border-blue-200 text-left text-xs font-medium text-blue-800">Vertragsdatum</th>
                              <th className="px-4 py-2 border-b border-blue-200 text-left text-xs font-medium text-blue-800">Anzahl der Anrufe</th>
                            </tr>
                          </thead>
                        </table>
                      </div>
                      <p className="mt-2 text-xs text-blue-700">
                        <strong>Tipp:</strong> Wenn keine Filter ausgewählt sind, werden alle Kunden der Kampagne exportiert.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="call-protocol" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone className="mr-2 h-5 w-5 text-indigo-500" />
                  Anrufprotokoll
                </h2>
                <p className="text-gray-700">
                  Das Anrufprotokoll ermöglicht es Ihnen, wichtige Informationen zu jedem Kundengespräch zu dokumentieren.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Verwendung des Anrufprotokolls</h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Wählen Sie einen Kunden aus der Kundenliste aus.</li>
                    <li>Im Kundendetailbereich finden Sie das Anrufprotokoll-Formular.</li>
                    <li>Geben Sie relevante Informationen zum Gespräch ein.</li>
                    <li>Wählen Sie ein Feedback (positiv, neutral oder negativ) aus.</li>
                    <li>Wählen Sie einen Status für den Kunden (offen, verkauft oder geschlossen).</li>
                    <li>Klicken Sie auf "Speichern", um das Protokoll zu speichern.</li>
                  </ol>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Vorteile des Anrufprotokolls</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Dokumentation aller Kundengespräche</li>
                    <li>Nachverfolgung des Kundenstatus</li>
                    <li>Erfassung von Kundenfeedback</li>
                    <li>Unterstützung bei der Analyse von Kampagnenerfolgen</li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>Tipp:</strong> Ausführliche Notizen im Anrufprotokoll helfen anderen Teammitgliedern, den Kundenkontakt besser zu verstehen.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="call-recordings" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone className="mr-2 h-5 w-5 text-indigo-500" />
                  Anrufaufzeichnungen
                </h2>
                <p className="text-gray-700">
                  Das System kann Anrufaufzeichnungen aus dem 3CX-Telefonsystem synchronisieren und mit Kundendaten verknüpfen.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Zugriff auf Anrufaufzeichnungen</h3>
                  <p className="text-gray-700 mb-3">
                    Anrufaufzeichnungen sind im Anrufverlauf eines Kunden verfügbar:
                  </p>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Wählen Sie eine Kampagne im CRM-Hauptbildschirm aus.</li>
                    <li>Klicken Sie auf einen Kunden, um dessen Details anzuzeigen.</li>
                    <li>Im Bereich "Anrufverlauf" werden alle Anrufe mit diesem Kunden angezeigt.</li>
                    <li>Alle Anrufe haben eine Wiedergabe-Schaltfläche, aber nur bei verfügbaren Aufzeichnungen ist diese aktiviert.</li>
                    <li>Klicken Sie auf die aktivierte Wiedergabe-Schaltfläche, um die Aufzeichnung abzuspielen.</li>
                    <li>Ein Audio-Player wird angezeigt, mit dem Sie die Aufzeichnung steuern können.</li>
                    <li>Klicken Sie auf das X-Symbol, um den Player zu schließen.</li>
                  </ol>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Hinweis:</strong> Nicht alle Anrufe haben Aufzeichnungen. Die Verfügbarkeit hängt von den Einstellungen des 3CX-Systems ab.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="reports-statistics" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart className="mr-2 h-5 w-5 text-indigo-500" />
                  Berichte und Statistiken
                </h2>
                <p className="text-gray-700">
                  Das CRM-System bietet Statistiken und Berichte, um die Leistung von Kampagnen und Agenten zu überwachen.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Statistikfunktionen</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <span className="font-medium">Anrufstatistiken</span>: Zeigen detaillierte Informationen über Anrufe, einschließlich Anzahl und Datum der Anrufe.
                    </li>
                    <li>
                      <span className="font-medium">Zeitraumfilter</span>: Ermöglicht die Auswahl eines bestimmten Zeitraums für die Statistikanalyse.
                    </li>
                    <li>
                      <span className="font-medium">Benutzerfilter</span>: Administratoren können Statistiken für bestimmte Benutzer anzeigen.
                    </li>
                    <li>
                      <span className="font-medium">Kampagnenfilter</span>: Ermöglicht die Filterung von Statistiken nach bestimmten Kampagnen.
                    </li>
                  </ul>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Zugriff auf Statistiken</h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Klicken Sie auf den Menüpunkt "Statistiken" in der Hauptnavigation.</li>
                    <li>Wählen Sie den gewünschten Zeitraum mit den Datumsfeldern aus.</li>
                    <li>Administratoren können einen bestimmten Benutzer aus dem Dropdown-Menü auswählen oder globale Statistiken anzeigen.</li>
                    <li>Wählen Sie optional eine bestimmte Kampagne aus dem Dropdown-Menü.</li>
                    <li>Die Statistiken werden automatisch aktualisiert, wenn die Filter geändert werden.</li>
                  </ol>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>Hinweis:</strong> Normale Benutzer können nur ihre eigenen Statistiken sehen, während Administratoren Zugriff auf die Statistiken aller Benutzer haben.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <section id="password-change" className="mb-10">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Settings className="mr-2 h-5 w-5 text-indigo-500" />
                  Passwort ändern
                </h2>
                <p className="text-gray-700">
                  Benutzer können ihr Passwort jederzeit ändern, um die Sicherheit ihres Kontos zu gewährleisten.
                </p>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-800 mb-2">Schritte zur Passwortänderung</h3>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>Klicken Sie auf Ihren Benutzernamen in der oberen rechten Ecke des Bildschirms.</li>
                    <li>Wählen Sie "Passwort ändern" aus dem Dropdown-Menü.</li>
                    <li>Geben Sie Ihr aktuelles Passwort ein.</li>
                    <li>Geben Sie Ihr neues Passwort ein.</li>
                    <li>Bestätigen Sie Ihr neues Passwort durch erneute Eingabe.</li>
                    <li>Klicken Sie auf "Passwort ändern", um die Änderungen zu übernehmen.</li>
                  </ol>
                </div>
                
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        <strong>Sicherheitshinweis:</strong> Das neue Passwort muss mindestens 8 Zeichen lang sein. Verwenden Sie eine Kombination aus Buchstaben, Zahlen und Sonderzeichen für ein sicheres Passwort.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              
              <div className="border-t border-gray-200 pt-6 mt-10">
                <p className="text-sm text-gray-500 text-center">
                  Letzte Aktualisierung: {new Date().toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Pulsante "Scroll to Top" */}
      {showScrollTop && (
        <button 
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 transition-all duration-300 z-50 flex items-center justify-center"
          aria-label="Zum Seitenanfang scrollen"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default Documentation; 